
<template>
  <v-dialog
    :model-value="isDialogOpen"
    max-width="780"
    persistent
    scrollable
  >
    <v-card
      class="rb-dialog"
      rounded="lg"
      elevation="8"
      color="surface" 
    >
      <v-card-title class="d-flex align-center ga-3 pa-4">
        <v-avatar
          color="rb-primary-light"
          rounded="lg"
          size="40"
        >
          <v-icon
            :icon="prependIcon"
            color="primary"
            size="22"
          />
        </v-avatar>

        <div class="flex-grow-1 overflow-hidden">
          <div class="text-subtitle-1 font-weight-bold text-truncate">
            {{ dialog?.title || "Details" }}
          </div>

          <div
            v-if="dialog?.subtitle"
            class="text-caption text-medium-emphasis text-truncate"
          >
            {{ dialog.subtitle }}
          </div>
        </div>

        <v-chip
          v-if="hasStatus"
          :color="isActive ? 'success' : 'error'"
          variant="tonal"
          size="small"
          class="font-weight-medium"
        >
          <v-icon
            start
            size="14"
            :icon="
              isActive
                ? 'mdi-check-circle-outline'
                : 'mdi-close-circle-outline'
            "
          />
          {{ isActive ? "Active" : "Inactive" }}
        </v-chip>

        <v-btn
          prepend-icon="mdi-close"
          variant="text"
          color="error"
          aria-label="Close dialog"
          @click="close"
        >
          <v-tooltip activator="parent" location="top">
            Close
          </v-tooltip>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="dialog-body pa-5">
        <div class="dialog-content">
          <slot />
        </div>

        <template v-if="hasActivity">
          <v-divider class="my-5" />

          <div class="d-flex align-center ga-2 mb-3">
            <v-icon
              icon="mdi-history"
              color="primary"
              size="20"
            />

            <div>
              <div class="text-body-2 font-weight-bold">
                Activity Information
              </div>

              <div class="text-caption text-medium-emphasis">
                Record creation and update details
              </div>
            </div>
          </div>

          <v-row dense>
            <v-col
              v-if="dialog?.value?.createdBy"
              cols="12"
              sm="6"
            >
              <v-sheet
                color="surface-variant"
                rounded="lg"
                class="pa-3 h-100"
              >
                <div class="d-flex align-start ga-3">
                  <v-avatar
                    color="surface"
                    rounded="lg"
                    size="36"
                  >
                    <v-icon
                      icon="mdi-account-plus-outline"
                      color="info"
                      size="20"
                    />
                  </v-avatar>

                  <div class="flex-grow-1 overflow-hidden">
                    <div class="text-caption text-medium-emphasis">
                      Created By
                    </div>

                    <div class="text-body-2 font-weight-bold text-wrap">
                      {{ dialog.value.createdBy }}
                    </div>

                    <div
                      class="d-flex align-center ga-1 mt-1 text-caption text-medium-emphasis"
                    >
                      <v-icon
                        icon="mdi-calendar-clock-outline"
                        size="14"
                      />
                      {{ displayDate(dialog.value.createdAt) }}
                    </div>
                  </div>
                </div>
              </v-sheet>
            </v-col>

            <v-col
              v-if="dialog?.value?.updatedBy"
              cols="12"
              sm="6"
            >
              <v-sheet
                color="surface-variant"
                rounded="lg"
                class="pa-3 h-100"
              >
                <div class="d-flex align-start ga-3">
                  <v-avatar
                    color="surface"
                    rounded="lg"
                    size="36"
                  >
                    <v-icon
                      icon="mdi-account-edit-outline"
                      color="violet"
                      size="20"
                    />
                  </v-avatar>

                  <div class="flex-grow-1 overflow-hidden">
                    <div class="text-caption text-medium-emphasis">
                      Updated By
                    </div>

                    <div class="text-body-2 font-weight-bold text-wrap">
                      {{ dialog.value.updatedBy }}
                    </div>

                    <div
                      class="d-flex align-center ga-1 mt-1 text-caption text-medium-emphasis"
                    >
                      <v-icon
                        icon="mdi-calendar-clock-outline"
                        size="14"
                      />
                      {{ displayDate(dialog.value.updatedAt) }}
                    </div>
                  </div>
                </div>
              </v-sheet>
            </v-col>
          </v-row>
        </template>
      </v-card-text>

      <template v-if="!hideFooter">
        <v-divider />

        <v-card-actions class="dialog-footer pa-4">
          <div class="d-flex align-center ga-2 text-caption text-medium-emphasis footer-note">
            <v-icon
              icon="mdi-information-outline"
              size="16"
            />
            Review the information before saving.
          </div>

          <v-spacer />

          <v-btn
            variant="outlined"
            color="secondary"
            size="small"
            class="footer-btn"
            :disabled="loading"
            @click="close"
          >
            Cancel
          </v-btn>

          <v-btn
            color="primary"
            variant="flat"
            size="small"
            class="footer-btn save-btn"
            prepend-icon="mdi-content-save-outline"
            :loading="loading"
            :disabled="saveDisabled"
            @click="save"
          >
            Save Changes
          </v-btn>
        </v-card-actions>
      </template>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState } from "pinia";
import { useUiStore } from "@/store";
import { formatDateTime } from "@/shared/utills/date";

export default {
  name: "MidScreenDialog",

  emits: ["close", "save"],

  props: {
    prependIcon: {
      type: String,
      default: "mdi-tag-plus",
    },

    saveDisabled: {
      type: Boolean,
      default: false,
    },

    closeOnRouteBack: {
      type: Boolean,
      default: true,
    },

    hideFooter: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapState(useUiStore, {
      dialog: "getMidDialog",
      loading: "getLoading",
    }),

    isDialogOpen() {
      return Boolean(this.dialog?.isOpened);
    },

    hasStatus() {
      const value = this.dialog?.value;

      return (
        value != null &&
        Object.prototype.hasOwnProperty.call(value, "status") &&
        value.status !== null &&
        value.status !== undefined
      );
    },

    isActive() {
      const status = this.dialog?.value?.status;

      return (
        status === true ||
        status === 1 ||
        status === "active" ||
        status === "Active"
      );
    },

    hasActivity() {
      return Boolean(
        this.dialog?.value?.createdBy ||
        this.dialog?.value?.updatedBy
      );
    },
  },

  methods: {
    displayDate(value) {
      if (!value) return "-";

      return formatDateTime(value);
    },

    close() {
      if (this.closeOnRouteBack) {
        this.$router.back();
      }

      this.$closeDialog();
      this.$emit("close");
    },

    save() {
      if (this.loading || this.saveDisabled) return;

      this.$emit("save");
    },
  },
};
</script>

<style scoped>
.rb-dialog {
  max-height: 90vh;
  border: 1px solid rgb(var(--v-theme-rb-border));
}

.dialog-body {
  background: rgb(var(--v-theme-background));
}

.dialog-content {
  min-width: 0;
}

.dialog-footer {
  gap: 10px;
  flex-wrap: wrap;
}

.footer-btn {
  height: 36px !important;
  padding: 0 14px !important;
  font-size: 12px !important;
  font-weight: 600 !important;
}

.save-btn,
.save-btn:hover,
.save-btn:focus,
.save-btn:active {
  background: #FFB000 !important;
  color: #000000 !important;
}

.save-btn :deep(.v-btn__overlay) {
  opacity: 0 !important;
}

@media (max-width: 600px) {
  .dialog-footer {
    justify-content: flex-end;
  }

  .footer-note {
    width: 100%;
  }
}
.dialog-body {
  background: rgb(var(--v-theme-background)) !important;
}
</style>